import { useState } from "react";
import { Upload, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useWebsiteCategories } from "@/hooks/useWebsiteCategories";
import { WebsiteCategory } from "@/types/websiteCategories";

interface BulkUploadCategoriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingCategories: WebsiteCategory[];
}

// Helper function to get all category names including nested children
const getAllCategoryNames = (categories: WebsiteCategory[]): string[] => {
  const names: string[] = [];
  const traverse = (cats: WebsiteCategory[]) => {
    for (const cat of cats) {
      names.push(cat.name);
      if (cat.children && cat.children.length > 0) {
        traverse(cat.children);
      }
    }
  };
  traverse(categories);
  return names;
};

export const BulkUploadCategoriesDialog = ({
  open,
  onOpenChange,
  existingCategories,
}: BulkUploadCategoriesDialogProps) => {
  const { createCategory } = useWebsiteCategories();
  const [inputText, setInputText] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<{ added: number; skipped: number } | null>(null);

  const parsedCategories = inputText
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  const existingNames = getAllCategoryNames(existingCategories);
  const existingNormalized = new Set(
    existingNames.map((name) => name.toLowerCase())
  );

  const newCategories = parsedCategories.filter(
    (name) => !existingNormalized.has(name.toLowerCase())
  );

  const duplicates = parsedCategories.filter((name) =>
    existingNormalized.has(name.toLowerCase())
  );

  const handleUpload = async () => {
    if (newCategories.length === 0) return;

    setIsUploading(true);
    setProgress(0);
    setResult(null);

    let added = 0;
    let skipped = duplicates.length;

    for (let i = 0; i < newCategories.length; i++) {
      try {
        await createCategory({
          name: newCategories[i],
          icon: "Globe",
          color: "#6B7280",
        });
        added++;
      } catch (error) {
        console.error(`Failed to add category: ${newCategories[i]}`, error);
        skipped++;
      }
      setProgress(Math.round(((i + 1) / newCategories.length) * 100));
    }

    setIsUploading(false);
    setResult({ added, skipped });
  };

  const handleClose = () => {
    setInputText("");
    setProgress(0);
    setResult(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Bulk Upload Categories
          </DialogTitle>
          <DialogDescription>
            Paste category names below, one per line. Duplicates will be skipped.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="py-6 text-center space-y-4">
            <CheckCircle className="w-12 h-12 mx-auto text-green-500" />
            <div>
              <p className="text-lg font-medium">Upload Complete</p>
              <p className="text-muted-foreground">
                Added {result.added} categories, skipped {result.skipped} duplicates
              </p>
            </div>
            <Button onClick={handleClose}>Done</Button>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <Textarea
                placeholder="Arts & Entertainment
Business & Economy
Education
..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                className="min-h-[200px] font-mono text-sm"
                disabled={isUploading}
              />

              {parsedCategories.length > 0 && (
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant="default">{newCategories.length} new</Badge>
                    {duplicates.length > 0 && (
                      <Badge variant="secondary" className="flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {duplicates.length} duplicates
                      </Badge>
                    )}
                  </div>

                  {duplicates.length > 0 && (
                    <p className="text-muted-foreground">
                      Duplicates will be skipped: {duplicates.slice(0, 3).join(", ")}
                      {duplicates.length > 3 && ` and ${duplicates.length - 3} more`}
                    </p>
                  )}
                </div>
              )}

              {isUploading && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading categories...
                  </div>
                  <Progress value={progress} />
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={isUploading}>
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={newCategories.length === 0 || isUploading}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Add {newCategories.length} Categories
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
